import { designationByRoleId } from '../../constants/designations';
import type { IPerson } from '../../store/slices/auth';
import type {
  Task,
  TaskCustomerData,
  TaskHistory,
  TaskMerchantPersonData,
} from '../../store/slices/tasks';
import type { ICustomer } from '../../store/slices/customers';

export const parseTaskId = (value: unknown): number | undefined => {
  if (typeof value !== 'number' && typeof value !== 'string') return undefined;
  if (typeof value === 'string' && !value.trim()) return undefined;
  const id = Number(value);
  return Number.isSafeInteger(id) ? id : undefined;
};

export const findTaskCustomer = <T extends Pick<ICustomer, 'person_external_id'>>(
  customerId: number | string | null | undefined,
  customers: T[]
): T | undefined => {
  const id = parseTaskId(customerId);
  if (id === undefined) return undefined;
  return customers.find((customer) => parseTaskId(customer.person_external_id) === id);
};

export const customerChangeIsSaved = (
  task: Task,
  customer: TaskCustomerData
): boolean => {
  const customerId = parseTaskId(task.customer_id);
  return customerId != null && customerId === parseTaskId(customer.externalId);
};

export const assigneeChangeIsSaved = (
  task: Task,
  person: TaskMerchantPersonData,
  history: TaskHistory[],
  previousHistoryIds: Set<string>
): boolean => {
  // Location person IDs and task-service IDs are different. Confirm the saved
  // name alongside a new assignment event, rather than comparing these IDs.
  if (parseTaskId(task.person_id_assign) == null || !person.name?.trim()) return false;
  if (task.person_name_assign?.trim() !== person.name.trim()) return false;
  return history.some(
    (entry) =>
      entry.history_id != null &&
      !previousHistoryIds.has(String(entry.history_id)) &&
      entry.history_type === 'assign'
  );
};

const ASSIGNEE_COLORS = [
  { backgroundColor: '#fce2d4', color: '#85452b' },
  { backgroundColor: '#e9e0f7', color: '#604387' },
  { backgroundColor: '#d9eee8', color: '#2d6659' },
  { backgroundColor: '#f6dfe7', color: '#88445d' },
  { backgroundColor: '#dce9f5', color: '#395f84' },
  { backgroundColor: '#eee7d8', color: '#75603c' },
];

export const taskAssigneeColor = (personId?: number, name?: string) => {
  const id = parseTaskId(personId);
  const normalizedName = name?.trim().toLowerCase();
  if (id === undefined && !normalizedName) {
    return { backgroundColor: '#f1f2f4', color: '#667085' };
  }
  const key =
    id ??
    Array.from(normalizedName || '').reduce(
      (hash, character) => (hash * 31 + character.charCodeAt(0)) >>> 0,
      0
    );
  return ASSIGNEE_COLORS[Math.abs(key) % ASSIGNEE_COLORS.length];
};

export const taskStatusKey = (status?: string) =>
  status
    ?.trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_') || 'unspecified';

export const taskStatusLabel = (
  status: string | undefined,
  translations: (key: string) => string
) => {
  const key = taskStatusKey(status);
  const statusKeys: Record<string, string> = {
    open: 'task_board.open',
    in_progress: 'task_board.in_progress',
    closed: 'task_board.closed',
    discarded: 'task_board.discarded',
    unspecified: 'task_board.unspecified',
  };
  return statusKeys[key]
    ? translations(statusKeys[key])
    : key
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

export const taskDesignationLabel = (
  designation: string,
  translations: (key: string) => string
) => {
  const designationKeys: Record<string, string> = {
    'Care Center Agent': 'task_board.care_center_agent',
    Nurse: 'task_board.nurse',
    'Consultation operator': 'task_board.consultation_operator',
    'Sales operator': 'task_board.sales_operator',
    'Diagnostics operator': 'task_board.diagnostics_operator',
    Doctor: 'task_board.doctor',
    'Medical staff': 'task_board.medical_staff',
    Admin: 'task_board.admin',
    Nutritionist: 'task_board.nutritionist',
  };
  return designationKeys[designation] ? translations(designationKeys[designation]) : designation;
};

export const taskInitials = (name?: string) =>
  name
    ?.trim()
    .split(/\s+/)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';

export const taskDate = (value?: string, timeOnly = false, language = 'en') => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return timeOnly
    ? date.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString(language);
};

export const taskDateInput = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

// Avoid comparing task-service IDs with location person IDs.
export const taskAssigneeDesignation = (task: Task, persons: IPerson[]) => {
  const assigneeId = parseTaskId(task.person_id_assign);
  const name = task.person_name_assign?.trim().toLowerCase();
  const personName = (person: IPerson) =>
    [person.person_firstname, person.person_lastname]
      .filter(Boolean)
      .join(' ')
      .trim()
      .toLowerCase();
  const byId =
    assigneeId === undefined
      ? undefined
      : persons.find(
          (person) =>
            parseTaskId(person.person_external_id) === assigneeId &&
            (!name || personName(person) === name)
        );
  const byName = name ? persons.filter((person) => personName(person) === name) : [];
  const person = byId || (byName.length === 1 ? byName[0] : undefined);
  return designationByRoleId(person?.role_id) || person?.role_name?.trim() || '';
};
